import React, { useEffect, useState, useRef } from "react";
import { InterpreterIcon, OrganizationsIcon, PlusIcon } from "../../assets/svgs/icons";
import { BsPlayFill } from "react-icons/bs";
import { PencilLine } from "lucide-react";
import moment from "moment";
import { useSelector } from "react-redux";
import { selectCurrentRole } from "../../store/auth/authSlice";
import AssignmentModal from "../Modals/AssignmentModal";
import AssignInterpreterModal from "../Modals/AssignInterpreterModal";
import {
  getAssignmentOrganizationId,
  getAssignmentQuestionId,
  getAssignmentResponseRecord,
} from "../../utils/interpreterAssignment";
import { getInterpreterImageUrl } from "../../pages/admin/Interpreter/helpers";
import { pickMediaUrl } from "../../utils/formTabs";

const looksLikeVideoUrl = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;

  return (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.startsWith("blob:") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("/") ||
    /\.(mp4|webm|mov|m4v)(\?|$)/i.test(trimmed)
  );
};

const parseSurveyJson = (value) => {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed || looksLikeVideoUrl(trimmed)) return null;
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return null;

  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
};

const videoFromSurveyPayload = (payload) => {
  if (!payload) return null;
  if (looksLikeVideoUrl(payload)) return payload.trim();

  const responses = payload.responses;
  if (!responses || typeof responses !== "object") {
    return (
      pickMediaUrl(payload.video) ||
      pickMediaUrl(payload.url) ||
      pickMediaUrl(payload.value) ||
      null
    );
  }

  for (const key of Object.keys(responses)) {
    const responseData = responses[key];
    if (
      responseData?.type === "record-video" &&
      responseData.value
    ) {
      return responseData.value;
    }
    if (looksLikeVideoUrl(responseData?.value)) {
      return responseData.value.trim();
    }
  }

  return null;
};

const videoFromUnknownResponse = (value) => {
  if (looksLikeVideoUrl(value)) return value.trim();

  const parsed = parseSurveyJson(value);
  if (parsed) return videoFromSurveyPayload(parsed);

  if (value && typeof value === "object") {
    return (
      videoFromSurveyPayload(value) ||
      videoFromUnknownResponse(value.response) ||
      pickMediaUrl(value.video) ||
      pickMediaUrl(value.url)
    );
  }

  return null;
};

const AssignedInterpreterAvatar = ({ interpreter }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const avatarUrl = getInterpreterImageUrl(interpreter);
  const showImage = Boolean(avatarUrl) && !imgFailed;

  useEffect(() => {
    setImgFailed(false);
  }, [avatarUrl]);

  return (
    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-gradient-to-br from-gray-50 to-gray-500">
      {!showImage && <InterpreterIcon />}
      {showImage ? (
        <img
          src={avatarUrl}
          alt={interpreter?.user?.full_name}
          className="h-full w-full rounded-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : null}
    </div>
  );
};

const OrganizationLogo = ({ organization }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const logoUrl = pickMediaUrl(organization?.logo);
  const showImage = Boolean(logoUrl) && !imgFailed;

  useEffect(() => {
    setImgFailed(false);
  }, [logoUrl]);

  return (
    <div className="relative flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-300 bg-gradient-to-br from-gray-50 to-gray-500">
      {!showImage && <OrganizationsIcon />}
      {showImage ? (
        <img
          src={logoUrl}
          alt={organization?.name || "Organisation"}
          className="h-full w-full object-contain p-0.5 drop-shadow-sm"
          onError={() => setImgFailed(true)}
        />
      ) : null}
    </div>
  );
};

const AssignmentCard = ({
  assignment,
  showFooter = true,
  showAssignContainer = true,
  onAssign,
  onTranslationSubmitted,
  interpreter,
}) => {
  const userRole = useSelector(selectCurrentRole);
  const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
  const [interpreterModalOpen, setInterpreterModalOpen] = useState(false);
  const [videoDuration, setVideoDuration] = useState("00:00");
  const videoRef = useRef(null);

  // Show edit button only for admin
  const showEditButton = userRole === "admin";

  const getVideoUrl = () => {
    const fromResponse = videoFromUnknownResponse(assignment?.response);
    if (fromResponse) return fromResponse;

    if (assignment?.responses && Array.isArray(assignment.responses)) {
      for (const responseItem of assignment.responses) {
        const fromItem = videoFromUnknownResponse(responseItem?.response);
        if (fromItem) return fromItem;

        const fromQuestion = pickMediaUrl(responseItem?.question?.video);
        if (fromQuestion) return fromQuestion;
      }
    }

    return (
      pickMediaUrl(assignment?.question?.video) ||
      pickMediaUrl(assignment?.video) ||
      pickMediaUrl(assignment?.videoUrl) ||
      null
    );
  };

  // Get video URL
  const videoUrl = getVideoUrl();

  // Status styles mapping
  const getStatusStyle = (status) => {
    const normalizedStatus = status?.toLowerCase();
    const styles = {
      complete: "bg-[#2ECC71] text-white",
      overdue: "bg-red-500 text-white",
      assigned: "bg-yellow text-black",
      not_active: "bg-[#FF8A00] text-white",
      not_assigned: "bg-[#FF8A00] text-white",
    };
 
    return styles[normalizedStatus] || "bg-gray-100 text-gray-700";
  };

  // Format status text
  const getStatusText = (status) => {
    const statusMap = {
      complete: "Completed",
      completed: "Completed",
      finished: "Completed",
      submitted: "Completed",
      not_active: "Unassigned",
      not_assigned: "Unassigned",
      assigned: "Assigned",
      overdue: "Overdue",
    };
    return statusMap[status?.toLowerCase()] || status;
  };

  // Simplified video duration detection
  useEffect(() => {
    if (videoUrl) {
      // Format duration helper function
      const formatDuration = (seconds) => {
        if (!seconds || isNaN(seconds) || seconds <= 0) return "00:01";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins.toString().padStart(2, "0")}:${secs
          .toString()
          .padStart(2, "0")}`;
      };

      // Create a simple metadata loader
      const video = document.createElement("video");

      // Add a timestamp to avoid caching issues
      const timestamp = new Date().getTime();
      video.src = `${videoUrl}?t=${timestamp}`;
      video.preload = "metadata";

      // Set simple event handlers
      video.onloadedmetadata = () => {
        setVideoDuration(formatDuration(video.duration));
      };

      video.onerror = () => {
        console.warn("Video metadata loading failed");
        setVideoDuration("00:01"); // Default fallback
      };

      // Set a backup timeout in case events don't fire
      const timeoutId = setTimeout(() => {
        if (video.duration > 0) {
          setVideoDuration(formatDuration(video.duration));
        } else {
          setVideoDuration("00:01"); // Default fallback
        }
      }, 2000);

      // Try to load metadata
      video.load();

      // Cleanup
      return () => {
        clearTimeout(timeoutId);
        video.onloadedmetadata = null;
        video.onerror = null;
        video.src = "";
      };
    }
  }, [videoUrl]);

  const handleCardClick = (e) => {
    if (!e.target.closest("button")) {
      setAssignmentModalOpen(true);
    }
  };

  const handleAssignClick = (e) => {
    e.stopPropagation();
    setInterpreterModalOpen(true);
  };

  const getQuestionId = () => getAssignmentQuestionId(assignment);

  const getOrganizationId = () => getAssignmentOrganizationId(assignment);

  // Get the question title with multiple fallbacks
  const getQuestionTitle = () => {
    // Direct question object with title or description
    if (assignment?.question) {
      if (typeof assignment.question === "object") {
        return (
          assignment.question.title ||
          assignment.question.description ||
          "Video Question"
        );
      }
    }

    // Question in responses array - structured as shown in screenshot
    if (
      assignment?.responses &&
      Array.isArray(assignment.responses) &&
      assignment.responses.length > 0
    ) {
      const firstResponse = assignment.responses[0];

      if (firstResponse?.question) {
        // If question is a nested object
        if (typeof firstResponse.question === "object") {
          // Return title if it exists, otherwise description, fallback to 'Video Question'
          return (
            firstResponse.question.title ||
            firstResponse.question.description ||
            "Video Question"
          );
        }
      }
    }

    return "Video Question";
  };

  // Get form name with fallbacks
  const getFormName = () => {
    // Direct form in question
    if (assignment?.question?.form?.name) {
      return assignment.question.form.name;
    }

    // Form in responses
    if (
      assignment?.responses &&
      Array.isArray(assignment.responses) &&
      assignment.responses.length > 0
    ) {
      const firstResponse = assignment.responses[0];
      if (firstResponse?.form?.name) {
        return firstResponse.form.name;
      }

      // Handle string form reference
      if (typeof firstResponse?.form === "string") {
        return "Form";
      }
    }

    return "Form";
  };

  // Get campaign name with fallbacks
  const getCampaignName = () => {
    // From question.form.campaign
    if (assignment?.question?.form?.campaign?.name) {
      return assignment.question.form.campaign.name;
    }

    // From responses
    if (assignment?.responses && assignment.responses.length > 0) {
      const firstResponse = assignment.responses[0];
      if (firstResponse?.form?.campaign?.name) {
        return firstResponse.form.campaign.name;
      }
    }

    return "No Campaign";
  };

  // Get question title and form info
  const questionTitle = getQuestionTitle();
  const formName = getFormName();
  const campaignName = getCampaignName();
  const organizationData =
    assignment?.organization || assignment.question?.form?.organization;

  // Handle play/pause for video
  const togglePlay = (e) => {
    e.stopPropagation();
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current
          .play()
          .catch((err) => console.error("Error playing video:", err));
      } else {
        videoRef.current.pause();
      }
    }
  };

  // Create an enriched assignment object that includes the videoUrl and extracted data
  const enrichedAssignment = {
    ...assignment,
    videoUrl,
    extractedQuestion: {
      title: questionTitle,
      _id: getQuestionId(),
    },
    extractedForm: {
      name: formName,
      _id:
        assignment?.form?._id ||
        (typeof assignment?.form === "string" ? assignment.form : null) ||
        assignment?.question?.form?._id ||
        assignment?.responses?.[0]?.form?._id ||
        assignment?.response?.form?._id,
    },
    extractedOrganization: organizationData,
  };

  // Add cleanup function for modal close
  const handleModalClose = () => {
    // Pause the video in the card when modal closes
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
    setAssignmentModalOpen(false);
  };

  return (
    <>
      <div
        className="bg-white rounded-xl overflow-hidden border p-1 cursor-pointer hover:shadow-lg transition-shadow"
        onClick={handleCardClick}
      >
        <div className="relative h-48">
          {videoUrl ? (
            <>
              <video
                ref={videoRef}
                className="w-full h-full object-cover rounded-xl"
                preload="metadata"
                autoPlay
                playsInline
                muted
                loop
                // controls={false}
              >
                <source
                  src={`${videoUrl}?t=${new Date().getTime()}`}
                  type="video/mp4"
                />
                Your browser does not support the video tag.
              </video>
              <div
                className="absolute inset-0 flex items-center justify-center"
                onClick={togglePlay}
              >
                <button className="bg-black/40 rounded-full h-11 w-11 border-0 flex items-center justify-center">
                  <BsPlayFill className="text-white" size={28} />
                </button>
              </div>
              {/* <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded"> */}
              {/* {videoDuration} */}
              {/* </div> */}
            </>
          ) : (
            // Fallback when no video is available
            <div className="w-full h-full bg-gray-200 rounded-xl flex items-center justify-center">
              <span className="text-gray-500">No video available</span>
            </div>
          )}
        </div>

        {/* {assignment._id} */}
        <div>{/* {videoUrl} */}</div>
        {/* Content Section */}
        <div className="p-2 space-y-4">
          <div className="flex items-center gap-2">
            <OrganizationLogo organization={organizationData} />
            <div className="text-xs text-gray-500">
              <div className="font-medium mb-1">{organizationData?.name}</div>
              <div>
                {campaignName}
                <span className="mx-1">›</span>
                {formName}
              </div>
            </div>
          </div>

          {/* Footer Section - Show for admin or if showFooter is true */}
          {(showFooter || userRole === "admin") && (
            <>
              {/* Interpreter Section - Show assigned interpreters */}
              {assignment?.assignment && assignment.assignment.length > 0 ? (
                <div className="space-y-2">
                  {assignment.assignment.map((interpreter, index) => (
                    <div
                      key={interpreter._id || index}
                      className="flex items-center justify-between border rounded-full p-1"
                    >
                      <div className="flex items-center gap-3">
                        <AssignedInterpreterAvatar interpreter={interpreter} />
                        <div>
                          <div className="font-medium text-xs">
                            {interpreter?.user?.full_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {interpreter?.user?.email}
                          </div>
                        </div>
                      </div>
                      {showEditButton && assignment?.status !== "complete" && (
                        <PencilLine size={18} onClick={handleAssignClick} />
                      )}
                    </div>
                  ))}
                </div>
              ) : null}
              {/* {videoUrl} */}
              <div>{/* {assignment._id} */}</div>
              {/* Status and Date Section */}
              <div className="flex justify-between items-center flex-end">
                <div className="text-xs text-gray-500">
                  {assignment?.completed_date
                    ? `Completed Date: ${moment(
                        assignment.completed_date
                      ).format("DD MMM YYYY")}`
                    : assignment?.deadline
                    ? `Deadline: ${moment(assignment.deadline).format(
                        "DD MMM YYYY"
                      )}`
                    : assignment?.date_created
                    ? `Created: ${moment(assignment.date_created).format(
                        "DD MMM YYYY, h:mm A "
                      )}`
                    : assignment?.responses &&
                      assignment.responses[0]?.date_created
                    ? `Created: ${moment(
                        assignment.responses[0].date_created
                      ).format("DD MMM YYYY")}`
                    : assignment?.response?.date_created
                    ? `Created: ${moment(
                        assignment.response.date_created
                      ).format("DD MMM YYYY")}`
                    : null}
                </div>
                <div className="text-end ">
                  <span
                    className={`px-3 py-1 rounded-full text-sm ${getStatusStyle(
                      assignment?.status
                    )}`}
                  >
                    {assignment?.status
                      ? getStatusText(assignment?.status)
                      : "Unassigned"}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <AssignmentModal
        visible={assignmentModalOpen}
        onClose={handleModalClose}
        onAssign={onAssign}
        onTranslationSubmitted={onTranslationSubmitted}
        status={getStatusText(assignment?.status)}
        assignment={enrichedAssignment}
        interpreter={interpreter}
      />

      <AssignInterpreterModal
        visible={interpreterModalOpen}
        onClose={() => setInterpreterModalOpen(false)}
        onSuccess={onAssign}
        questionId={getQuestionId()}
        organizationId={getOrganizationId()}
        assignment={enrichedAssignment}
        videoRefs={{
          current: videoRef.current
            ? {
                [getAssignmentResponseRecord(assignment)?._id]: videoRef.current,
              }
            : {},
        }}
      />
    </>
  );
};

export default AssignmentCard;
